import * as React from "react";
import {
  Avatar,
  Box,
  Button,
  Card,
  CircularProgress,
  Grid,
  Stack,
  Typography,
} from "@mui/joy";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import MenuBookRoundedIcon from "@mui/icons-material/MenuBookRounded";
import PushPinRoundedIcon from "@mui/icons-material/PushPinRounded";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import { Link as RouterLink, useParams } from "react-router-dom";
import { useTheme } from "@mui/joy/styles";
import MDEditor from "@uiw/react-md-editor";

import { getAdminReadingNote, ReadingNote } from "../api";
import PageHero from "../components/common/PageHero";

function formatDate(value?: string) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleString();
}

export default function AdminReadingNoteDetailPage() {
  const { id } = useParams();
  const theme = useTheme();
  const [note, setNote] = React.useState<ReadingNote | null>(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!id) {
        setNote(null);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const data = await getAdminReadingNote(id);
        if (!cancelled) {
          setNote(data ?? null);
        }
      } catch (error) {
        if (!cancelled) {
          setNote(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [id]);

  const createdLabel = note ? formatDate(note.createdAt) : "";
  const heroDescription = note
    ? `Shared ${createdLabel ? `on ${createdLabel}` : "recently"}. Celebrate this reflection and edit whenever inspiration strikes.`
    : "Preview the devotional article and ensure it’s ready for seekers exploring the library.";

  let content: React.ReactNode;

  if (loading) {
    content = (
      <Card
        variant="outlined"
        sx={{ borderRadius: "xl", p: { xs: 2.5, md: 3 }, display: "flex", justifyContent: "center" }}
      >
        <Stack spacing={1} alignItems="center" sx={{ py: 4 }}>
          <CircularProgress size="sm" />
          <Typography level="body-sm" sx={{ color: "text.secondary" }}>
            Loading article…
          </Typography>
        </Stack>
      </Card>
    );
  } else if (!note) {
    content = (
      <Card variant="outlined" sx={{ borderRadius: "xl", p: { xs: 2.5, md: 3 } }}>
        <Stack spacing={1.5} alignItems="center" textAlign="center">
          <Avatar variant="soft" color="primary" sx={{ width: 56, height: 56, borderRadius: "lg" }}>
            <MenuBookRoundedIcon />
          </Avatar>
          <Typography level="title-sm" sx={{ fontWeight: 600 }}>
            Article not found
          </Typography>
          <Typography level="body-sm" sx={{ color: "text.secondary", maxWidth: 360 }}>
            It may have been removed or shared under a different link. Return to the library to explore other reflections.
          </Typography>
          <Button
            component={RouterLink}
            to="/articles"
            variant="outlined"
            color="neutral"
            startDecorator={<ArrowBackRoundedIcon />}
          >
            Back to library
          </Button>
        </Stack>
      </Card>
    );
  } else {
    content = (
      <Grid container spacing={2.5} alignItems="stretch">
        <Grid xs={12} md={8}>
          <Card
            variant="outlined"
            sx={{
              borderRadius: "xl",
              p: { xs: 2.5, md: 3 },
              gap: 2,
            }}
          >
            <Stack spacing={0.75}>
              <Typography level="title-md" sx={{ fontWeight: 700 }}>
                Article preview
              </Typography>
              <Typography level="body-sm" sx={{ color: "text.secondary" }}>
                This is how devotees will experience the reflection in the reading library.
              </Typography>
            </Stack>

            <Box
              data-color-mode={theme.palette.mode === "dark" ? "dark" : "light"}
              sx={{
                borderRadius: "lg",
                border: "1px solid",
                borderColor: "divider",
                overflow: "hidden",
                backgroundColor: "background.surface",
                "& .wmde-markdown": {
                  fontSize: "1rem",
                  lineHeight: 1.75,
                },
              }}
            >
              <MDEditor.Markdown source={note.content} />
            </Box>
          </Card>
        </Grid>

        <Grid xs={12} md={4}>
          <Stack spacing={2.5} sx={{ height: "100%" }}>
            <Card variant="soft" color="primary" sx={{ borderRadius: "xl", p: 2.5, gap: 1.25 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <MenuBookRoundedIcon />
                <Typography level="title-sm" sx={{ fontWeight: 600 }}>
                  Article summary
                </Typography>
              </Stack>
              <Typography level="body-md" sx={{ color: "rgba(255,255,255,0.88)" }}>
                {note.title}
              </Typography>
              <Typography level="body-sm" sx={{ color: "rgba(255,255,255,0.72)" }}>
                {createdLabel ? `Published ${createdLabel}` : "Publication date pending"}
              </Typography>
            </Card>

            <Card variant="outlined" sx={{ borderRadius: "xl", p: 2.5, gap: 1.5 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <AccessTimeRoundedIcon fontSize="small" />
                <Typography level="title-sm" sx={{ fontWeight: 600 }}>
                  Library status
                </Typography>
              </Stack>
              <Stack spacing={0.75}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <PushPinRoundedIcon fontSize="small" />
                  <Typography level="body-sm" sx={{ fontWeight: 600 }}>
                    {note.pinned ? "Pinned for seekers" : "Available on demand"}
                  </Typography>
                </Stack>
                <Typography level="body-sm" sx={{ color: "text.secondary" }}>
                  {note.pinned
                    ? "This reflection appears in the pinned guidance carousel."
                    : "Pin the article from the library to highlight it for all devotees."}
                </Typography>
              </Stack>
            </Card>
          </Stack>
        </Grid>
      </Grid>
    );
  }

  return (
    <Box>
      <PageHero
        eyebrow="Sacred study"
        title={note ? note.title : "Article details"}
        description={heroDescription}
        actions={
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <Button
              component={RouterLink}
              to="/articles"
              variant="outlined"
              color="neutral"
              startDecorator={<ArrowBackRoundedIcon />}
            >
              Back to library
            </Button>
            {note ? (
              <Button
                component={RouterLink}
                to={`/articles/${note.id}/edit`}
                startDecorator={<EditRoundedIcon />}
              >
                Edit article
              </Button>
            ) : null}
          </Stack>
        }
      />

      {content}
    </Box>
  );
}
